/**
 * Proactive Token Refresh Scheduler
 *
 * Instead of waiting for a 401 error, this module decodes the access token's
 * `exp` claim and schedules a silent refresh 5 minutes before it expires.
 *
 * Usage: call `startTokenRefreshScheduler()` once after login / app init.
 *        call `stopTokenRefreshScheduler()` on logout.
 */

import { readStoredTokens, storeAuthTokens } from '../model/authStorage';
import { decodeJwtPayload } from './auth.domain';
import { refreshToken } from '../api/auth.api';
import { sessionRevokedNotifier, sessionEndReasonFromCode } from './session-revoked-notifier';
import type { StoredTokens } from '../model/auth.types';

/** How many seconds before expiry to trigger a proactive refresh */
const REFRESH_BUFFER_SECONDS = 5 * 60; // 5 minutes

/** Minimum delay to avoid tight loops (e.g. if token already near expiry) */
const MIN_SCHEDULE_MS = 10_000; // 10 seconds

/**
 * `setTimeout` stores its delay as a signed 32-bit int; any delay larger than
 * this overflows to a negative value and fires *immediately*. A token with a
 * very long lifetime (e.g. a mis-configured multi-day JWT) would then trigger an
 * infinite refresh loop, so we cap the scheduled delay here.
 */
const MAX_TIMEOUT_MS = 2 ** 31 - 1; // ~24.8 days

let timerId: ReturnType<typeof setTimeout> | null = null;

/**
 * Shared in-flight refresh promise — used by both the scheduler and the
 * 401 interceptor so they never send two refresh requests simultaneously.
 */
let sharedRefreshPromise: Promise<StoredTokens> | null = null;

/** Minimal shape of the Web Locks API we rely on (avoids depending on lib.dom's LockManager typings). */
type LockRequester = { request<T>(name: string, cb: () => Promise<T>): Promise<T> };

const LOCK_NAME = 'auth-token-refresh';

/**
 * Perform the refresh, but first re-read storage: under a one-time-use refresh
 * token policy a sibling tab may have already rotated the token while we waited
 * for the lock. If the stored access token is still comfortably valid, reuse it
 * instead of firing a second refresh — presenting an already-consumed token would
 * trip FusionAuth's reuse detection and force-log-out every tab.
 */
async function runRefresh(
  refreshTokenValue: string,
  staleAccessToken: string | null
): Promise<StoredTokens> {
  const current = readStoredTokens();
  const payload = decodeJwtPayload(current?.access_token);
  const msUntilExpiry = payload?.exp ? (payload.exp as number) * 1000 - Date.now() : 0;

  // Reuse only when the stored token changed while we waited for the lock (a
  // sibling tab rotated it). If it's still the just-rejected token, the server
  // refused a time-valid token (clock skew / revocation) — reusing never recovers.
  if (
    current?.refresh_token &&
    current.access_token !== staleAccessToken &&
    msUntilExpiry > REFRESH_BUFFER_SECONDS * 1000
  ) {
    return current; // a sibling tab already refreshed — reuse its tokens
  }

  const newTokens = await refreshToken(current?.refresh_token ?? refreshTokenValue);
  const afterRefresh = readStoredTokens();
  // A sibling tab may have logged out (cleared storage) while this refresh was in
  // flight — don't resurrect the session by writing the rotated tokens back.
  if (!afterRefresh?.refresh_token) {
    throw new Error('Session cleared during refresh');
  }
  // A sibling tab may have logged in while we awaited the refresh (login writes in
  // replace-mode and doesn't take this lock). Writing our merged tokens would clobber
  // their fresh session with the OLD session's rotated token — bail and keep what's
  // stored. `sub` catches a DIFFERENT user; `sid` also catches the SAME user re-logging
  // in (a new login mints a new `sid`, but `sid` is stable across a refresh rotation),
  // which a sub-only check missed — the fresh login's token would be overwritten and, if
  // the new login superseded the old session, the stored token would be dead on arrival.
  const afterPayload = decodeJwtPayload(afterRefresh.access_token);
  const newPayload = decodeJwtPayload(newTokens.access_token);
  const differentUser = afterPayload?.sub && newPayload?.sub && afterPayload.sub !== newPayload.sub;
  const differentSession =
    afterPayload?.sid && newPayload?.sid && afterPayload.sid !== newPayload.sid;
  if (differentUser || differentSession) {
    return afterRefresh;
  }
  storeAuthTokens(newTokens); // persist inside the lock so the next tab sees it
  return newTokens;
}

/**
 * Serialize refreshes across tabs via the Web Locks API so multiple open tabs
 * never rotate the refresh token in parallel. Falls back to a direct refresh
 * where Web Locks is unavailable.
 */
function refreshWithCrossTabLock(
  refreshTokenValue: string,
  staleAccessToken: string | null
): Promise<StoredTokens> {
  const locks = getLockRequester();
  if (!locks) {
    return runRefresh(refreshTokenValue, staleAccessToken);
  }
  return locks.request(LOCK_NAME, () => runRefresh(refreshTokenValue, staleAccessToken));
}

/** Access the Web Locks API (or undefined where unsupported). */
function getLockRequester(): LockRequester | undefined {
  return typeof navigator !== 'undefined' && 'locks' in navigator
    ? (navigator as unknown as { locks?: LockRequester }).locks
    : undefined;
}

/**
 * Run `fn` serialized against token refreshes — via the SAME cross-tab Web Lock the
 * refresh path uses — so a logout can't interleave with an in-flight rotation (which
 * would revoke the wrong refresh token, or resurrect storage right after it's cleared).
 * Where Web Locks is unavailable, at least await this tab's in-flight refresh first.
 */
export async function withAuthRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = getLockRequester();
  if (locks) {
    return locks.request(LOCK_NAME, fn);
  }
  await sharedRefreshPromise?.catch(() => {});
  return fn();
}

/**
 * Initiate a token refresh, or join an existing in-flight one.
 * Returns the new tokens on success, throws on failure.
 *
 * Two layers of de-duplication:
 *   - `sharedRefreshPromise` collapses concurrent refreshes within THIS tab.
 *   - the Web Lock inside `refreshWithCrossTabLock` collapses them ACROSS tabs.
 */
export function getOrStartRefresh(refreshTokenValue: string): Promise<StoredTokens> {
  if (!sharedRefreshPromise) {
    // Snapshot the token being replaced before the lock wait (see runRefresh).
    const staleAccessToken = readStoredTokens()?.access_token ?? null;
    sharedRefreshPromise = refreshWithCrossTabLock(refreshTokenValue, staleAccessToken)
      .then((tokens) => {
        // R8: a fresh token is now available — covers the store / reuse / sibling-return
        // paths. Attached ONCE here (not per-caller) so N concurrent 401s that join this
        // shared promise don't each fire the event. Listeners: the core socket re-handshakes
        // if it had connected anonymously. Decoupled via a window event to avoid a
        // @modules/auth ↔ @services/core import cycle.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth:token-refreshed'));
        }
        return tokens;
      })
      .finally(() => {
        sharedRefreshPromise = null;
      });
  }
  return sharedRefreshPromise;
}

/**
 * Get the number of milliseconds until the access token expires.
 * Returns `null` if the token is missing or has no `exp` claim.
 */
function getMsUntilExpiry(): number | null {
  const tokens = readStoredTokens();
  const payload = decodeJwtPayload(tokens?.access_token);
  if (!payload?.exp) return null;

  const expiresAtMs = (payload.exp as number) * 1000;
  return expiresAtMs - Date.now();
}

/**
 * Perform the actual silent refresh and reschedule.
 */
async function doRefresh(): Promise<void> {
  try {
    const tokens = readStoredTokens();
    if (!tokens?.refresh_token) {
      stopTokenRefreshScheduler();
      return;
    }

    // getOrStartRefresh persists the rotated tokens (inside the cross-tab lock).
    await getOrStartRefresh(tokens.refresh_token);
    scheduleNextRefresh();
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 401 || status === 400) {
      // Definitive failure. Surface the session-ended modal proactively so an idle
      // tab (which never fires a guarded request) still learns it was kicked:
      // 401 SESSION_SUPERSEDED → "signed in elsewhere", USER_DEACTIVATED → account
      // disabled by an admin, otherwise the refresh token is dead → "expired".
      const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
      stopTokenRefreshScheduler();
      sessionRevokedNotifier.notify(sessionEndReasonFromCode(code) ?? 'expired');
      return;
    }
    // R4: a "session cleared during refresh" (logout mid-flight) has no HTTP status and
    // would otherwise re-arm the 30s retry forever. If storage is gone, stop for good.
    if (!readStoredTokens()?.refresh_token) {
      stopTokenRefreshScheduler();
      return;
    }
    timerId = setTimeout(() => void doRefresh(), 30_000);
  }
}

/**
 * Calculate when to fire the next refresh and set the timer.
 */
function scheduleNextRefresh(): void {
  if (timerId !== null) {
    clearTimeout(timerId);
    timerId = null;
  }

  const msUntilExpiry = getMsUntilExpiry();
  if (msUntilExpiry === null) {
    return;
  }

  const rawDelay = msUntilExpiry - REFRESH_BUFFER_SECONDS * 1000;
  const delayMs = Math.min(Math.max(rawDelay, MIN_SCHEDULE_MS), MAX_TIMEOUT_MS);
  timerId = setTimeout(() => void doRefresh(), delayMs);
}

/**
 * Start the proactive refresh scheduler.
 * Safe to call multiple times — it will reset the timer.
 */
export function startTokenRefreshScheduler(): void {
  scheduleNextRefresh();
}

/**
 * Stop the scheduler (e.g. on logout).
 */
export function stopTokenRefreshScheduler(): void {
  if (timerId !== null) {
    clearTimeout(timerId);
    timerId = null;
  }
}
